import { Button, Dialog, Tag, Tooltip, Typography } from "@hellocarbo/lasagne";
import { Plus, Trash2 } from "lucide-react";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { useFetcher } from "react-router";

import { PortalContainerContext } from "@/contexts";
import { TTrajectoryAction } from "@/types";
import { cn } from "@/utils/classes";

function ActionTags({
  className,
  reduction_factor,
  recommended,
  category,
}: {
  className?: string;
  reduction_factor: TTrajectoryAction["reduction_factor"];
  recommended: TTrajectoryAction["recommended"];
  category: TTrajectoryAction["category"];
}) {
  const { t } = useTranslation(["formatter", "trajectory", "categories"]);

  return (
    <div className={cn("gap-x-2xs flex items-center", className)}>
      <Tag
        intent="secondary"
        text={t("formatter:percentage", { value: reduction_factor })}
      />
      {recommended && (
        <Tag intent="secondary" text={t("trajectory:actionPlan.recommended")} />
      )}
      <Tag text={t(`categories:${category}`)} />
    </div>
  );
}

export interface ActionListItemProps
  extends Omit<
    TTrajectoryAction,
    "id" | "title_fr" | "title_en" | "content_fr" | "content_en"
  > {
  title: string;
  content: string;
  isReadOnly?: boolean;
  actionId: number;
}

function ActionListItem({
  title,
  content,
  category,
  recommended,
  reduction_factor,
  examples,
  isReadOnly = true,
  status,
  actionId,
}: ActionListItemProps) {
  const { container } = use(PortalContainerContext);

  const { t } = useTranslation(["trajectory"]);
  const fetcher = useFetcher();

  const isAvailable = status === "available";

  return (
    <li className="gap-x-xs flex w-full items-center">
      <Dialog
        portalContainer={container as Element}
        trigger={
          <button className="border-grayscale-xx-light px-lg py-md w-full rounded-lg border text-left shadow-sm">
            <Typography step="-1" weight="bold">
              {title}
            </Typography>
            <ActionTags
              reduction_factor={reduction_factor}
              recommended={recommended}
              category={category}
              className="mt-xs"
            />
          </button>
        }
        size="large"
        title={title}
      >
        <ActionTags
          reduction_factor={reduction_factor}
          recommended={recommended}
          category={category}
          className="mb-xl"
        />
        <Typography step="1" weight="bold" as="h5" className="mb-sm">
          {t("trajectory:actionPlan.actionDescription")}
        </Typography>
        <Typography className="mb-md leading-lg" as="div">
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </Typography>
        {examples.length ? (
          <>
            <Typography weight="bold" className="mb-3xs">
              {t("trajectory:actionPlan.theyDidIt")}
            </Typography>
            <ul className="gap-x-sm flex items-center">
              {examples.map((example) => (
                <li key={example.label}>
                  <Button
                    intent="secondary"
                    shape="link"
                    as="a"
                    href={example.url}
                    shouldOpenNewTab
                    size="small"
                  >
                    {example.label}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Dialog>
      {!isReadOnly && (
        <Tooltip
          portalContainer={container as Element}
          content={t(
            `trajectory:actionPlan.${isAvailable ? "addAction" : "removeAction"}`
          )}
          triggerAsChild
        >
          <Button
            size="small"
            shape="soft"
            intent={isAvailable ? "positive" : "negative"}
            Icon={isAvailable ? Plus : Trash2}
            onClick={() =>
              fetcher.submit(
                {
                  action: isAvailable ? "add" : "remove",
                  actionId: actionId,
                },
                { method: "PATCH" }
              )
            }
            isLoading={fetcher.state === "submitting"}
            disabled={fetcher.state !== "idle"}
          />
        </Tooltip>
      )}
    </li>
  );
}

export default ActionListItem;
